import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import type { DashboardDataResponse } from 'api/models/DashboardDataResponse';
import { FirmsService } from 'api/services/FirmsService';
import {
  FIRM_DASHBOARD_DATA_DEFAULT_ERROR_MESSAGE,
  FIRM_DASHBOARD_DATA_QUERY_KEY,
  FIRM_DASHBOARD_DATA_STALE_TIME,
  SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/firms';
import type { DefaultQueryResponse } from 'common/types/services';
import { throwError } from 'utilities';
import type { SummaryDashboardType } from './useGenerateDashboard';

export type { DashboardDataResponse };

export interface UseGetDashboardDataParams {
  firmId?: number;
  definitionId?: number;
  summaryType?: SummaryDashboardType;
  measurementDate?: string;
  fundId?: number;
  shouldQueryAutomatically?: boolean;
}

// One hook for both surfaces rather than the two the spreadsheet has: the dashboard already
// threads summaryType through as a prop, so the branch belongs here (same shape as
// useGenerateDashboard) instead of duplicating the hook.
const getDashboardData = async (
  firmId: number,
  definitionId: number,
  summaryType: SummaryDashboardType,
  measurementDate?: string,
  fundId?: number
): Promise<DashboardDataResponse | null> => {
  try {
    const result =
      summaryType === 'valuations'
        ? await FirmsService.apiFirmsValuationsDefinitionsDashboardDataRead(
          String(firmId),
          String(definitionId),
          measurementDate || undefined,
          fundId
        )
        : await FirmsService.apiFirmsSummaryDefinitionsDashboardDataRead(
          String(firmId),
          String(definitionId),
          measurementDate || undefined,
          fundId
        );

    if (!result) {
      return throwError(FIRM_DASHBOARD_DATA_DEFAULT_ERROR_MESSAGE);
    }

    return result;
  } catch (error) {
    return throwError(FIRM_DASHBOARD_DATA_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetDashboardData = (
  params: UseGetDashboardDataParams
): DefaultQueryResponse<DashboardDataResponse> => {
  const {
    firmId,
    definitionId,
    summaryType = 'firm',
    measurementDate,
    fundId,
    shouldQueryAutomatically = true,
  } = params;

  const { data, error, isError, isFetched, isFetching, isLoading, refetch } = useQuery<
    DashboardDataResponse | null | undefined,
    Error
  >({
    enabled: !isNil(firmId) && !isNil(definitionId) && shouldQueryAutomatically,
    // Keeps the previous numbers on screen while a fund or measurement-date change refetches.
    // Without it every tile blanks to a skeleton on each filter change, which reads as broken.
    placeholderData: keepPreviousData,
    queryFn: () =>
      getDashboardData(firmId!, definitionId!, summaryType, measurementDate, fundId),
    // summaryType is part of the key so the summary and valuations surfaces cannot collide on
    // the same (firmId, definitionId) pair.
    queryKey: [
      FIRM_DASHBOARD_DATA_QUERY_KEY,
      summaryType,
      firmId,
      definitionId,
      measurementDate,
      fundId,
    ],
    refetchOnWindowFocus: SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
    staleTime: FIRM_DASHBOARD_DATA_STALE_TIME,
  });

  return {
    data,
    error,
    isError,
    isFetched,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetDashboardData;
